import { ChangeEvent, FunctionComponent, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { HighlightedSearchesServices } from "../../services/highlightedSearches";
import { ProductServices } from "../../services/products";
import { useAsync } from "../../utils/hooks/useAsync";
import useDevice from "../../utils/hooks/useDevice";
import Loading from "../common/Loading";
import { ScrollToTopOnMount } from "../common/ScrollToTopOnMount";
import Footer from "../Footer";
import MostWantedCard from "../MostWantedCard";
import MultiSelect from "../MultiSelect";
import ProductCard from "../ProductCard";

type Props = {
  subcategoryOptions: MultiSelectOption[];
};

const SearchContainer: FunctionComponent<Props> = ({ subcategoryOptions }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [highlightedSearches, setHighlightedSearches] = useState<Search[]>();

  const [params, setParams] = useSearchParams();
  const [isCustom, setIsCustom] = useState<boolean>(false);

  const { friendlyUrl } = useParams();
  const { run, isLoading, isSuccess } = useAsync();
  const materialOptions: MultiSelectOption[] = [
    { value: "all", label: "Todos" },
    { value: "amarillo", label: "Oro Amarillo" },
    { value: "rojo", label: "Oro Rojo" },
    { value: "blanco", label: "Oro Blanco" },
    { value: "plata", label: "Plata" },
  ];

  const handleInitFilterParams = () => {
    const keys = params.keys();
    const firstKey = keys.next();
    const paramFilters: ProductFilters = {
      subcategory: [],
      material: [],
    };
    [];

    if (!firstKey.done) {
      const matchFilter = (
        filterOptions: MultiSelectOption[],
        value: string
      ) => {
        const currentFilter = filterOptions.filter((fo) =>
          value.includes(fo.value)
        );
        return currentFilter[0];
      };

      params.forEach((value, key) => {
        const handleAddFilterOption = (
          filter: MultiSelectOption[],
          options: MultiSelectOption[]
        ) => {
          const filterOption = matchFilter(options, value);
          if (filterOption) {
            filter.push(filterOption);
          }
        };

        switch (key) {
          case "sub":
            handleAddFilterOption(paramFilters.subcategory, subcategoryOptions);
            break;
          case "material":
            handleAddFilterOption(paramFilters.material, materialOptions);
        }
      });
    }
    return paramFilters;
  };
  const [filters, setFilters] = useState<ProductFilters>(
    handleInitFilterParams()
  );

  useEffect(() => {
    setFilters(handleInitFilterParams);
  }, [params]);

  useEffect(() => {
    run(HighlightedSearchesServices.getHighlightedSearchs()).then((data: any) =>
      setHighlightedSearches(data)
    );
  }, []);

  useEffect(() => {
    const materialFilter =
      filters.material.length > 0 && filters.material[0].value !== "all"
        ? filters.material[0].value
        : undefined;

    const subcategoryFilter = [];
    filters.subcategory.length > 0 && filters.subcategory[0].value !== "all"
      ? subcategoryFilter.push(filters.subcategory[0].value)
      : undefined;

    run(
      ProductServices.getProducts({
        query: friendlyUrl,
        color: materialFilter,
        subcategory: subcategoryFilter,
        custom: isCustom
      })
    ).then((data: any) => {
      setProducts(data?.data || []);
    });
  }, [filters, friendlyUrl, isCustom]);

  const handleParams = (
    value: string,
    filter: Filter | null,
    mode: "multiselect" | "monoselect" | "page"
  ) => {
    const conditionalFunctions = {
      page: () => {
        params.set("page", value);
        setParams(params);
      },
      monoselect: () => {
        if (filter === "material") {
          const materialValue =
            value.includes("all") || value.includes("plata")
              ? value
              : `oro-${value}`;
          if (filter) params.set(filter, materialValue);
          setParams(params);
        } else {
          const filteredValues = params
            .getAll("sub")
            .filter((x) => x !== value);

          params.delete("sub");
          params.set("sub", value);

          setParams(params);
        }
      },
      multiselect: {
        valueNotExist: () => {
          if (value != "all") {
            params.append("sub", value);
            setParams(params);
          } else {
            params.delete("sub");
            setParams(params);
          }
        },
        valueExist: () => {
          const filteredValues = params
            .getAll("sub")
            .filter((x) => x !== value);

          if (params.getAll("sub").length <= 1) {
            params.delete("sub");
          } else {
            params.delete("sub");
            filteredValues.forEach((value) => {
              params.append("sub", value);
            });
          }
          setParams(params);
        },
      },
    };

    const checkValueExistence =
      params.getAll("sub").includes(value) === false
        ? "valueNotExist"
        : "valueExist";

    if (mode === "monoselect" || mode === "page") {
      conditionalFunctions[mode]();
    } else {
      conditionalFunctions[mode][checkValueExistence]();
    }
  };

  const handleFilterChange = (
    e: ChangeEvent<HTMLInputElement>,
    filter: Filter,
    options: MultiSelectOption[],
    mode: "multiselect" | "monoselect" | "page"
  ) => {
    const { value, checked } = e.target;
    handleParams(value, filter, mode);
    const currentCheckedOption = options.find((o) => o.value === value);

    if (mode === "monoselect") {
      setFilters({ ...filters, [filter]: [currentCheckedOption] });
    } else {
      const content = filters[filter];
      const currentFilter = content.some((content) => content.value === value)
        ? content.filter((f) => f.value !== value)
        : [...content, currentCheckedOption];

      setFilters({ ...filters, [filter]: currentFilter });
    }
  };

  const handleCleanFilter = () => {
    setIsCustom(false);
    setFilters({ material: [], subcategory: [] });
    setParams("");
  };

  const { width } = useDevice();

  return (
    <>
      <ScrollToTopOnMount />
      <section className="search_container">
        <header className="search_header">
          <div className="header_results">
            <p>Resultados de la búsqueda:</p>
          </div>
          <div className="header_filters">
            <h1>{friendlyUrl}</h1>
            {(filters.material.length > 0 || filters.subcategory.length > 0) &&
              width < 768 && (
                <p
                  onClick={handleCleanFilter}
                  className="filters_delete_mobile"
                >
                  Borrar filtros
                </p>
              )}
            <div className="filters_selects">
              {(filters.material.length > 0 ||
                filters.subcategory.length > 0) && (
                <p onClick={handleCleanFilter} className="filters_delete">
                  Borrar filtros
                </p>
              )}
              {/*<Switch
                setState={setIsCustom}
                state={isCustom}
                label={"Personalizable"}
                />*/}
              <MultiSelect
                options={materialOptions}
                title="Material"
                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                  handleFilterChange(
                    e,
                    "material",
                    materialOptions,
                    "monoselect"
                  )
                }
                selectedOptions={filters.material}
              />
              {subcategoryOptions && (
                <MultiSelect
                  options={subcategoryOptions}
                  title="Subcategorías"
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    handleFilterChange(
                      e,
                      "subcategory",
                      subcategoryOptions,
                      "monoselect"
                    )
                  }
                  selectedOptions={filters.subcategory}
                />
              )}
            </div>
          </div>
        </header>
        {products && isSuccess ? (
          <>
            <section className="search_products">
              {products.map((prod, index) => {
                return (
                  <ProductCard
                    key={index}
                    title={prod.title}
                    img={prod.thumbnailUrl}
                    friendlyUrl={prod.friendlyUrl}
                    sku={prod.sku}
                  />
                );
              })}
            </section>
            <section className="most_wanted_section">
              <h1>Más buscados</h1>
              {highlightedSearches ? (
                highlightedSearches.map((s, index) => {
                  return (
                    <div className="most_wanted_card">
                      <MostWantedCard name={s.search} key={index} />
                    </div>
                  );
                })
              ) : isLoading ? (
                <Loading />
              ) : null}
            </section>
          </>
        ) : isLoading ? (
          <Loading />
        ) : null}
      </section>
    </>
  );
};

export default SearchContainer;
